/**
 * "What's holding this up" — plain-English blocker derivation for the office
 * job page (Issue #301).
 *
 * The signals that stop a job from flowing are scattered across the page (the
 * stage strip, the PO card, the parts card, the invoice, the status chip), so
 * a novice can't tell WHY a job is stuck. This pure helper reads the data the
 * page already fetches and returns one ranked, human-first list: each entry is
 * a single sentence ("what's hanging it up") + one action ("the button that
 * unclogs it"). No new endpoint, no mutation — a derivation only.
 *
 * The parts blocker mirrors the SERVER dispatch parts gate exactly
 * (`src/server/services/dispatchPartsGate.ts`): a repair job can't be
 * dispatched while a linked purchase order sits in any non-terminal status
 * (everything except `closed`/`cancelled`, per `PO_BLOCKING_STATUSES`). That
 * gate is real today — the office just never saw it until a 409 fired when
 * they tried to schedule. This surfaces it up front.
 */

// ── Inputs (structural — mirror the page's response shapes) ────────────────

export interface BlockerJobLike {
    status: string;
    scheduled_start?: string | null;
    /** `trades.code` — only device-bearing trades can be "missing devices". */
    trade_code?: string | null;
    /** Hard deadline (DATE). A vendor WO's complete-by lands here too
     *  (woJobWiring.ts `complete_by_date` → due_by_date). */
    due_by_date?: string | null;
}

export interface BlockerPoLike {
    id: string;
    po_number: string | null;
    status: string;
    confirmed_delivery_date?: string | null;
    expected_delivery_date?: string | null;
}

export interface BlockerInvoiceLike {
    id: string;
    status: string;
    total_amount?: number | string | null;
    /** Successful sends on the ledger (#1142). An emailed invoice stays
     *  'open' until paid, so status alone can't say whether it went out. */
    emailed_count?: number | null;
}

// ── Output ─────────────────────────────────────────────────────────────────

/** blocked = stops forward motion now · attention = needs a decision · info =
 *  a loose end (money not yet out the door). */
export type BlockerSeverity = "blocked" | "attention" | "info";

export type BlockerAction =
    | { kind: "po"; label: string; poId: string }
    | { kind: "invoice"; label: string; invoiceId: string }
    | { kind: "schedule"; label: string }
    | { kind: "reschedule"; label: string }
    | { kind: "resume"; label: string }
    | { kind: "add_devices"; label: string }
    /** #1545 — reopen the resend dialog seeded with just the people who missed
     *  the last packet. */
    | { kind: "resend_missed"; label: string }
    /** #1669 — a job check-up problem: opens the Help Desk on it. */
    | { kind: "help_desk"; label: string };

export interface JobBlocker {
    id: string;
    severity: BlockerSeverity;
    /** Short label, e.g. "Waiting on parts". */
    title: string;
    /** One plain sentence: the why + what to do. */
    detail: string;
    action?: BlockerAction;
}

export interface JobBlockersResult {
    blockers: JobBlocker[];
    /** Shown only when `blockers` is empty — the calm "on track" line. */
    onTrack: string;
}

// PO statuses that block dispatch — the exact complement of the server's
// terminal set (poStatusMachine.ts TERMINAL). Any PO not in a terminal status
// means the parts aren't in hand yet.
const PO_TERMINAL = new Set(["closed", "cancelled"]);

const UNSCHEDULED_STATUSES = new Set(["draft", "awaiting_scheduling", "unqueued"]);

const POST_VISIT = new Set(["in_progress", "office_review", "completed", "closed", "cancelled"]);

/** Once the truck is rolling the booked day is moot — too late to move it. */
const VISIT_UNDERWAY = new Set(["en_route", "arrived"]);

/** Trades whose jobs are meaningless without devices (mirrors the server's
 *  `tradeFamilyForCode` in services/jobAssetTradeFilter.ts — keep in sync). */
const DEVICE_BEARING_TRADES = new Set(["backflow", "fire_ext"]);

function money(value: number): string {
    return `$${Math.round(value).toLocaleString()}`;
}

/** YYYY-MM-DD (a DATE column) → "Jul 25"; split rather than `new Date()` so a
 *  UTC-midnight parse can't shift the day in a negative-offset timezone. */
function shortDate(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return null;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mo = months[Number(m[2]) - 1];
    return mo ? `${mo} ${Number(m[3])}` : null;
}

/** A timestamp's LOCAL calendar day as YYYY-MM-DD — the day the office sees
 *  on the board, which is what gets compared against a DATE deadline. */
function localYmd(iso: string): string | null {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function humanStatus(s: string): string {
    return s.replace(/_/g, " ");
}

/** The one friendly "here's where it stands" line for a job with no blockers. */
function onTrackLine(status: string): string {
    switch (status) {
        case "scheduled":
            return "On track — it's on the board and ready to run.";
        case "en_route":
            return "On track — the tech is on the way.";
        case "arrived":
            return "On track — the tech is on site.";
        case "in_progress":
            return "On track — work is underway in the field.";
        case "office_review":
            return "On track — it's in Close Out waiting for review.";
        case "completed":
            return "On track — the work is done.";
        case "closed":
            return "All done — this job is closed.";
        case "cancelled":
            return "This job was cancelled.";
        default:
            return "Nothing's holding this up.";
    }
}

/**
 * Derive the ranked blocker list for a job. Pure — same inputs always give the
 * same output. Ranking (most-blocking first): no devices → parts →
 * cannot-complete → hold → unscheduled → booked after the due date → unsent
 * invoice.
 */
export function deriveJobBlockers(
    job: BlockerJobLike,
    purchaseOrders: BlockerPoLike[] = [],
    invoices: BlockerInvoiceLike[] = [],
    deviceCount: number | null = null,
    /** #1545 — how many recipients the job's most recent packet send failed to
     *  reach (a delivery that failed, or someone whose documents weren't ready
     *  so they got no email at all). Null when the caller has no send history
     *  loaded, which must read as "don't know", never as "nobody missed". */
    packetMissedOnLastSend: number | null = null,
): JobBlockersResult {
    const blockers: JobBlocker[] = [];
    const status = job.status;

    // Terminal jobs have nothing to unclog.
    if (status === "closed" || status === "cancelled") {
        return { blockers: [], onTrack: onTrackLine(status) };
    }

    // 0. No devices — ranked FIRST because it silently wastes the whole trip.
    //    A compliance job with an empty device list gives the tech nothing to
    //    test; the work-order paths used to produce these routinely and the
    //    only warning was Command Center pre-flight, which looks at jobs
    //    scheduled TODAY — i.e. the morning the truck rolls. Saying it here,
    //    from creation, is the point. `deviceCount === null` means the caller
    //    didn't supply a count (older callers), so we stay silent.
    if (
        deviceCount === 0 &&
        !POST_VISIT.has(status) &&
        DEVICE_BEARING_TRADES.has((job.trade_code ?? "").toLowerCase())
    ) {
        blockers.push({
            id: "no_devices",
            severity: "blocked",
            title: "No devices on this job",
            detail: "There's nothing for the tech to test — the job has no devices linked. Add the location's devices before this goes out.",
            action: { kind: "add_devices", label: "Add devices" },
        });
    }

    // 1. Waiting on parts — mirrors the server dispatch parts gate.
    const blockingPOs = purchaseOrders.filter((po) => !PO_TERMINAL.has(po.status));
    // Prefer the PO that needs office action (needs_review/draft) as the one to
    // point at; otherwise the earliest blocking PO in the list.
    const actionPO =
        blockingPOs.find((po) => po.status === "needs_review") ??
        blockingPOs.find((po) => po.status === "draft") ??
        blockingPOs[0];
    if (actionPO) {
        const poLabel = actionPO.po_number ?? "the purchase order";
        const arrival = shortDate(actionPO.confirmed_delivery_date);
        let detail: string;
        if (actionPO.status === "needs_review") {
            detail = `A purchase order (${poLabel}) needs review before the parts can be ordered. This job can't be dispatched until the parts are in hand.`;
        } else if (actionPO.status === "draft") {
            detail = `The parts purchase order (${poLabel}) is still a draft — send it so the parts get ordered. The job can't go out until they arrive.`;
        } else if (arrival) {
            detail = `Parts arrive ${arrival} (${poLabel}). The job can't be dispatched until they're in hand.`;
        } else {
            detail = `Parts haven't been received yet — ${poLabel} is ${humanStatus(actionPO.status)}. The job can't be dispatched until the parts arrive.`;
        }
        blockers.push({
            id: "parts",
            severity: "blocked",
            title: "Waiting on parts",
            detail,
            action: { kind: "po", label: "Open the PO", poId: actionPO.id },
        });
    }

    // 2. Cannot complete — the tech couldn't finish.
    if (status === "cannot_complete") {
        blockers.push({
            id: "cannot_complete",
            severity: "attention",
            title: "Marked can't complete",
            detail: "The tech couldn't finish this visit — it needs a new day on the board or an office decision.",
            action: { kind: "reschedule", label: "Reschedule" },
        });
    }

    // 3. On hold — someone paused it.
    if (status === "held") {
        blockers.push({
            id: "held",
            severity: "attention",
            title: "On hold",
            detail: "Someone paused this job, so it's off the schedule. Resume it when it's ready to move again.",
            action: { kind: "resume", label: "Resume" },
        });
    }

    // 4. Not scheduled yet — but suppress when parts are the real reason it
    //    isn't on the board (avoids telling a novice to "schedule it" when the
    //    parts gate would just bounce them).
    const hasPartsBlocker = blockers.some((b) => b.id === "parts");
    if (UNSCHEDULED_STATUSES.has(status) && !job.scheduled_start && !hasPartsBlocker) {
        blockers.push({
            id: "unscheduled",
            severity: "attention",
            title: "Not scheduled yet",
            detail: "This job isn't on anyone's day yet. Pick a date so it lands on the board.",
            action: { kind: "schedule", label: "Set schedule" },
        });
    }

    // 4.25 Booked after the due date. The visit is on the board, but on a day
    //      later than the job's hard deadline (#092678489: visit Nov 7, due
    //      Nov 1) — the page used to call that "On track" in green. Attention,
    //      not blocked: the office may have agreed the date with the customer,
    //      but it should be a decision, not a surprise.
    if (job.scheduled_start && job.due_by_date && !POST_VISIT.has(status) && !VISIT_UNDERWAY.has(status)) {
        const visitDay = localYmd(job.scheduled_start);
        const dueDay = job.due_by_date.slice(0, 10);
        if (visitDay && /^\d{4}-\d{2}-\d{2}$/.test(dueDay) && visitDay > dueDay) {
            blockers.push({
                id: "scheduled_after_due",
                severity: "attention",
                title: "Booked after the due date",
                detail: `Visit is ${shortDate(visitDay)}; due ${shortDate(dueDay)}.`,
                action: { kind: "schedule", label: "Change the date" },
            });
        }
    }

    // 4.5 Somebody never got the packet. Ranked above the unsent invoice
    //     because a person is actively waiting on paperwork they were promised
    //     — a compliance report their water authority is asking them for, or
    //     the invoice they've been told to expect. Until #1545 this fact was
    //     visible ONLY inside Close-Out, and only while the job was still there:
    //     the moment it was sent and settled, a half-delivered packet went
    //     completely quiet. `blocked` severity is deliberate; this is not a
    //     loose end, it is a promise the business hasn't kept yet.
    if (POST_VISIT.has(status) && packetMissedOnLastSend != null && packetMissedOnLastSend > 0) {
        const n = packetMissedOnLastSend;
        blockers.push({
            id: "packet_missed",
            severity: "blocked",
            title: `${n} ${n === 1 ? "person" : "people"} never got the packet`,
            detail:
                n === 1
                    ? "One recipient on the last send either bounced or received nothing at all — they're still waiting on their paperwork."
                    : `${n} recipients on the last send either bounced or received nothing at all — they're still waiting on their paperwork.`,
            action: { kind: "resend_missed", label: "Resend" },
        });
    }

    // 5. Invoice not out the door — only once the visit has happened.
    if (POST_VISIT.has(status)) {
        // Sent = paid/partially paid, OR the ledger shows it was emailed. A
        // customer packet that carried the invoice counts the same as a
        // direct "Send invoice" (job #092678160: the packet delivered the
        // invoice on 2026-10-06 and this still said "Invoice not sent").
        const emailed = (inv: BlockerInvoiceLike) => Number(inv.emailed_count ?? 0) > 0;
        const sent = invoices.some(
            (inv) => !["draft", "open"].includes((inv.status ?? "").toLowerCase()) || emailed(inv),
        );
        const draft = invoices.find(
            (inv) => ["draft", "open"].includes((inv.status ?? "").toLowerCase()) && !emailed(inv),
        );
        if (!sent && draft) {
            const total = draft.total_amount != null ? Number(draft.total_amount) : NaN;
            const amount = Number.isFinite(total) && total > 0 ? ` ${money(total)} is` : " It's";
            blockers.push({
                id: "unsent_invoice",
                severity: "info",
                title: "Invoice not sent",
                detail: `${amount} sitting in a draft invoice. Send it to the customer so this job can get paid.`,
                action: { kind: "invoice", label: "Send invoice", invoiceId: draft.id },
            });
        }
    }

    return { blockers, onTrack: onTrackLine(status) };
}

/**
 * #1669 — the server's job check-up problems as rows for the same list, so the
 * job page never says "on track" in one card while the check-up strip says
 * "can't go out yet" right above it. A check-up row whose id the page already
 * derived (e.g. `no_devices`) is dropped — one row per cause.
 */
export function blockersFromCheckup(
    problems: {
        id: string;
        severity: "blocked" | "attention";
        title: string;
        detail: string;
        fix?: { label: string };
    }[],
    alreadyDerived: JobBlocker[] = [],
): JobBlocker[] {
    const have = new Set(alreadyDerived.map((b) => b.id));
    return problems
        .filter((p) => !have.has(p.id))
        .map((p) => ({
            id: `checkup:${p.id}`,
            severity: p.severity,
            title: p.title,
            detail: p.detail,
            action: { kind: "help_desk", label: p.fix ? p.fix.label : "Show me" },
        }));
}

/**
 * Audit 2026-10-01 (Arlington #092678449): NEXT STEP said "Schedule" right
 * beside "Waiting on parts — can't be dispatched until they're in hand". The
 * server's dispatch parts gate would refuse that move, so the button is a
 * contradiction. True when a blocker on the list makes the primary action one
 * the server will bounce; the page then drops NEXT STEP and lets the
 * blocker's own button ("Open the PO") be the next step. Deliberately narrow:
 * only the parts gate, only forward-to-the-field moves.
 */
const DISPATCH_MOVES = new Set(["scheduled", "en_route", "arrived", "in_progress"]);

export function nextStepContradicted(
    primary: { kind: string; status?: string } | null,
    blockers: Pick<JobBlocker, "id">[],
): boolean {
    if (!primary || primary.kind !== "status" || !primary.status) return false;
    return DISPATCH_MOVES.has(primary.status) && blockers.some((b) => b.id === "parts");
}
